import { createContext } from "react";
import type { SessionErrorReason, SignedOutReason } from "./resolveSession";
import type { Seller } from "./seller";

export type SessionState =
  /** Asking core whether this browser already has a session. */
  | { status: "checking" }
  | { status: "signed-in"; seller: Seller }
  | { status: "signed-out"; reason: SignedOutReason }
  | { status: "error"; reason: SessionErrorReason };

export interface SessionContextValue {
  state: SessionState;
  /** Re-reads the session from core. Used by the error state; never changes what core decided. */
  retry: () => void;
  /**
   * Ends the session and returns to the signed-out surface.
   *
   * Resolves whether or not core answered — signing out must never fail
   * (`contracts/API.md` § Seller session shape) — and the state that follows comes from
   * re-reading `GET /v1/seller/me`, not from assuming the call worked.
   */
  signOut: () => Promise<void>;
  /**
   * True when the last sign-out could not be delivered to core.
   *
   * The `HttpOnly` cookie is still live in that case, so the founder is still signed in and
   * the desk is still theirs. Surfaces render this rather than the generic unreachable
   * panel, because "we could not reach Dealbid" and "you are not signed out" are different
   * facts and only the second one is actionable. Cleared by the next sign-out attempt.
   */
  signOutFailed: boolean;
}

export const SessionContext = createContext<SessionContextValue | null>(null);
